/**
 * Unusual-volume markers drawn IN the volume pane (attached to the CME
 * volume histogram):
 * - CME: the multiple ("2.4x") just above the CME bar (the bar itself is
 *   also drawn brighter, see ChartView's volume colours);
 * - Vest: a 🐋 on a small dark pill just above the thin Vest bar (its own
 *   price scale, so its top comes from the Vest series via
 *   `setVestCoordinate`).
 * `setShowLabels(false)` drops the multiples (the brighter bar and the 🐋
 * stay).
 * When both fire on one bar they never overlap: the higher anchor keeps its
 * spot and the other moves above it; at the pane top they sit side by side.
 */
import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import { drawEmojiBadge, emojiScale, resetGlyphState } from './emojiBadge';

export interface VolumeSpikeItem {
  /** Chart bar open, seconds. */
  time: number;
  /** CME bar: its volume (CME scale) and label. */
  cme?: { value: number; label: string };
  /** Vest bar: its top value on the Vest series' own scale. */
  vest?: { top: number };
}

export const LABEL_FONT_PX = 9;
export const WHALE_FONT_PX = 14;
const LABEL_H = LABEL_FONT_PX + 2;
/** Pill height (emojiBadge badgeBox: 1.3 x font). */
const WHALE_H = Math.round(WHALE_FONT_PX * 1.3);
const GAP = 2;
const TOP_MIN = 2;

export interface Box {
  x: number;
  /** Vertical centre. */
  y: number;
  h: number;
}

export interface MarkerLayout {
  label?: Box;
  whale?: Box;
}

export function labelWidth(label: string): number {
  return Math.ceil(label.length * LABEL_FONT_PX * 0.6);
}

/**
 * Positions (centres) for one bar. `cmeTopY` / `vestTopY` are the bar tops in
 * pane px (null when that marker is not shown).
 */
export function layoutVolumeMarkers(x: number, cmeTopY: number | null, vestTopY: number | null, label = ''): MarkerLayout {
  const out: MarkerLayout = {};
  if (cmeTopY !== null) out.label = { x, y: cmeTopY - GAP - LABEL_H / 2, h: LABEL_H };
  if (vestTopY !== null) out.whale = { x, y: vestTopY - GAP - WHALE_H / 2, h: WHALE_H };
  const { label: l, whale: w } = out;
  if (l && w) {
    const overlap = Math.abs(l.y - w.y) < (l.h + w.h) / 2 + 1;
    if (overlap) {
      // Keep the higher one; move the other above it.
      if (w.y <= l.y) w.y = l.y - (l.h + w.h) / 2 - 1;
      else l.y = w.y - (l.h + w.h) / 2 - 1;
    }
  }
  for (const b of [out.label, out.whale]) if (b && b.y - b.h / 2 < TOP_MIN) b.y = TOP_MIN + b.h / 2;
  if (l && w && Math.abs(l.y - w.y) < (l.h + w.h) / 2 + 1) {
    // Pinned to the top together: side by side instead.
    w.x = x + labelWidth(label) / 2 + WHALE_H / 2 + 4; // pill is a bit wider than tall
  }
  return out;
}

interface Placed extends MarkerLayout {
  time: number;
  labelText: string;
}

class Renderer implements IPrimitivePaneRenderer {
  private _items: Placed[] = [];
  update(items: Placed[]): void {
    this._items = items;
  }
  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      ctx.save();
      const k = emojiScale(scope.mediaSize?.width ?? 1000);
      for (const p of this._items) {
        if (p.label) {
          resetGlyphState(ctx);
          ctx.font = `bold ${LABEL_FONT_PX * vr}px Arial`;
          ctx.lineWidth = 3 * hr;
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
          ctx.strokeText(p.labelText, p.label.x * hr, p.label.y * vr);
          ctx.fillStyle = '#f8fafc';
          ctx.fillText(p.labelText, p.label.x * hr, p.label.y * vr);
        }
        if (p.whale) drawEmojiBadge(ctx, '🐋', p.whale.x, p.whale.y, WHALE_FONT_PX * k, hr, vr);
      }
      ctx.restore();
    });
  }
}

class PaneView implements IPrimitivePaneView {
  private _renderer = new Renderer();
  constructor(private _source: VolumeSpikeMarkersPrimitive) {}
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.place());
    return this._renderer;
  }
}

export class VolumeSpikeMarkersPrimitive implements ISeriesPrimitive<Time> {
  private _items: VolumeSpikeItem[] = [];
  private _view = new PaneView(this);
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _requestUpdate?: () => void;
  private _vestY: (value: number) => number | null = () => null;
  private _onPlaced?: (counts: { cme: number; vest: number }) => void;
  private _showLabels = true;

  attached(p: SeriesAttachedParameter<Time>): void {
    this._series = p.series;
    this._chart = p.chart;
    this._requestUpdate = p.requestUpdate;
  }
  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }
  paneViews(): IPrimitivePaneView[] {
    return [this._view];
  }
  updateAllViews(): void {
    /* recomputed on every render */
  }

  setItems(items: VolumeSpikeItem[]): void {
    this._items = items;
    this._requestUpdate?.();
  }
  items(): VolumeSpikeItem[] {
    return this._items;
  }
  setVestCoordinate(fn: (value: number) => number | null): void {
    this._vestY = fn;
    this._requestUpdate?.();
  }
  /** "2.4x" multiples on/off; spike highlighting and 🐋 are unaffected. */
  setShowLabels(on: boolean): void {
    if (this._showLabels === on) return;
    this._showLabels = on;
    this._requestUpdate?.();
  }
  showLabels(): boolean {
    return this._showLabels;
  }
  setOnPlaced(cb: ((counts: { cme: number; vest: number }) => void) | undefined): void {
    this._onPlaced = cb;
  }

  place(): Placed[] {
    const out: Placed[] = [];
    if (!this._series || !this._chart) return out;
    const ts = this._chart.timeScale();
    let cme = 0;
    let vest = 0;
    for (const it of this._items) {
      const x = ts.timeToCoordinate(it.time as Time);
      if (x === null) continue;
      const cy = it.cme && this._showLabels ? this._series.priceToCoordinate(it.cme.value) : null;
      const vy = it.vest ? this._vestY(it.vest.top) : null;
      if (cy === null && vy === null) continue;
      const labelText = this._showLabels ? it.cme?.label ?? '' : '';
      const l = layoutVolumeMarkers(x, cy, vy, labelText);
      if (l.label) cme++;
      if (l.whale) vest++;
      out.push({ ...l, time: it.time, labelText });
    }
    this._onPlaced?.({ cme, vest });
    return out;
  }
}
